/**
 * Alshallal Pastry Company HR System - Expiring Documents Report Page
 * Phase 19: In-app expiring documents view for HR
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  AlertCircle,
  Calendar,
  Clock,
  FileText,
  RefreshCw,
  User,
  Building2,
  Filter,
  Download
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ExpiringDocumentsPage = () => {
  const { token, user } = useAuth();
  const { isRTL } = useLanguage();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState('30');
  
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/admin/documents/expiring?window_days=${windowDays}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
    } catch (err) {
      console.error('Failed to fetch expiry report:', err);
      setError(isRTL ? 'فشل في تحميل التقرير' : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [token, windowDays, isRTL]);
  
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);
  
  const getExpiryBadgeStyle = (daysLeft) => {
    if (daysLeft < 0) return 'bg-red-100 text-red-800 border-red-200';
    if (daysLeft <= 7) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (daysLeft <= 30) return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };
  
  const renderDocumentRow = (doc) => (
    <TableRow key={doc.document_id}>
      <TableCell>
        <div className="flex items-center gap-2">
          {doc.scope === 'company' ? (
            <Building2 className="h-4 w-4 text-blue-500" />
          ) : (
            <User className="h-4 w-4 text-gray-500" />
          )}
          <div>
            <div className="font-medium">{doc.display_name || doc.document_type}</div>
            <div className="text-sm text-muted-foreground">{doc.category}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {doc.scope === 'company' ? (
          <Badge variant="outline" className="text-blue-600 border-blue-200">
            {isRTL ? 'شركة' : 'Company'}
          </Badge>
        ) : (
          <div>
            <div className="font-medium">{doc.employee_name || '—'}</div>
            {doc.employee_id && (
              <div className="text-xs text-muted-foreground">
                ID: {doc.employee_id.slice(0, 8)}...
              </div>
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        {doc.expiry_date ? format(new Date(doc.expiry_date), 'MMM d, yyyy') : '—'}
      </TableCell>
      <TableCell>
        <Badge className={cn("text-xs", getExpiryBadgeStyle(doc.days_until_expiry))}>
          {doc.days_until_expiry < 0 
            ? (isRTL ? `منتهي منذ ${Math.abs(doc.days_until_expiry)} يوم` : `Expired ${Math.abs(doc.days_until_expiry)} days ago`)
            : doc.days_until_expiry === 0
              ? (isRTL ? 'ينتهي اليوم' : 'Expires today')
              : (isRTL ? `${doc.days_until_expiry} يوم متبقي` : `${doc.days_until_expiry} days left`)
          }
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={doc.status === 'approved' ? 'default' : 'secondary'}>
          {doc.status}
        </Badge>
      </TableCell>
    </TableRow>
  );
  
  return (
    <div className="p-6 space-y-6" data-testid="expiring-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-8 w-8 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold">
              {isRTL ? 'المستندات المنتهية الصلاحية' : 'Expiring Documents Report'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isRTL ? 'مراجعة المستندات التي تنتهي صلاحيتها أو انتهت' : 'Review documents that are expiring or have expired'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={windowDays} onValueChange={setWindowDays}>
            <SelectTrigger className="w-[150px]" data-testid="window-days-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{isRTL ? '7 أيام' : '7 days'}</SelectItem>
              <SelectItem value="14">{isRTL ? '14 يوم' : '14 days'}</SelectItem>
              <SelectItem value="30">{isRTL ? '30 يوم' : '30 days'}</SelectItem>
              <SelectItem value="60">{isRTL ? '60 يوم' : '60 days'}</SelectItem>
              <SelectItem value="90">{isRTL ? '90 يوم' : '90 days'}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}
      
      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isRTL ? 'تنتهي قريباً' : 'Expiring Soon'}
                  </p>
                  <p className="text-3xl font-bold text-amber-600">
                    {report.expiring_soon_count}
                  </p>
                </div>
                <Clock className="h-10 w-10 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isRTL ? 'منتهية الصلاحية' : 'Already Expired'}
                  </p>
                  <p className="text-3xl font-bold text-red-600">
                    {report.expired_count}
                  </p>
                </div>
                <AlertCircle className="h-10 w-10 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isRTL ? 'إجمالي التنبيهات' : 'Total Alerts'}
                  </p>
                  <p className="text-3xl font-bold">
                    {report.expiring_soon_count + report.expired_count}
                  </p>
                </div>
                <FileText className="h-10 w-10 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Expired Documents */}
      {report && report.expired.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {isRTL ? `المستندات المنتهية (${report.expired.length})` : `Expired Documents (${report.expired.length})`}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'هذه المستندات انتهت صلاحيتها وتحتاج إلى تجديد' : 'These documents have expired and need renewal'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'المستند' : 'Document'}</TableHead>
                  <TableHead>{isRTL ? 'الموظف/الشركة' : 'Employee/Company'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead>{isRTL ? 'حالة الموافقة' : 'Approval'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.expired.map(renderDocumentRow)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      
      {/* Expiring Soon Documents */}
      {report && report.expiring_soon.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <Clock className="h-5 w-5" />
              {isRTL ? `تنتهي خلال ${windowDays} يوم (${report.expiring_soon.length})` : `Expiring in ${windowDays} Days (${report.expiring_soon.length})`}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'هذه المستندات ستنتهي صلاحيتها قريباً' : 'These documents will expire soon'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'المستند' : 'Document'}</TableHead>
                  <TableHead>{isRTL ? 'الموظف/الشركة' : 'Employee/Company'}</TableHead>
                  <TableHead>{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead>{isRTL ? 'حالة الموافقة' : 'Approval'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.expiring_soon.map(renderDocumentRow)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      
      {/* No Documents */}
      {report && report.expiring_soon.length === 0 && report.expired.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {isRTL ? 'لا توجد تنبيهات' : 'No Alerts'}
            </h3>
            <p className="text-slate-600">
              {isRTL 
                ? `لا توجد مستندات تنتهي خلال ${windowDays} يوم`
                : `No documents expiring within ${windowDays} days`}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Report Timestamp */}
      {report && (
        <p className="text-xs text-muted-foreground text-center">
          {isRTL ? 'تم إنشاء التقرير في' : 'Report generated at'}: {format(new Date(report.generated_at), 'PPpp')}
        </p>
      )}
    </div>
  );
};

export default ExpiringDocumentsPage;
